import { PDFDownloadLink } from "@react-pdf/renderer";
import React, { useEffect, useRef, useState } from "react";
import PDFReport from "../PDFReport/PDFReport";
import html2canvas from "html2canvas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const ReportSection = (reportData) => {
  const [chartImages, setChartImages] = useState({});
  const [imagesReady, setImagesReady] = useState(false);
  const chartRefs = useRef({});
  const reportSections = [
    {
      title: "User Metrics",
      metrics: [
        {
          label: "Total New Registrations",
          value: reportData?.reportData?.UserRecord?.reduce(
            (sum, cur) => sum + cur.total,
            0,
          ),
          date: new Date().toISOString(),
        },
        {
          label: "Active Users",
          value: reportData?.reportData?.UserRecord?.reduce(
            (sum, cur) => sum + cur.active,
            0,
          ),
          date: new Date().toISOString(),
        },
        {
          label: "Register male users",
          value: reportData?.reportData?.UserRecord?.reduce(
            (sum, cur) => sum + cur.male,
            0,
          ),
          date: new Date().toISOString(),
        },
        {
          label: "Active male users",
          value: reportData?.reportData?.UserRecord?.reduce(
            (sum, cur) => sum + cur.activeMale,
            0,
          ),
          date: new Date().toISOString(),
        },
        {
          label: "Register female users",
          value: reportData?.reportData?.UserRecord?.reduce(
            (sum, cur) => sum + cur.female,
            0,
          ),
          date: new Date().toISOString(),
        },
        {
          label: "Active female users",
          value: reportData?.reportData?.UserRecord?.reduce(
            (sum, cur) => sum + cur.activeFemale,
            0,
          ),
          date: new Date().toISOString(),
        },
      ],
    },
    {
      title: "Users Requests Summary",
      metrics: [
        {
          label: "Total Requests",
          value: reportData?.reportData?.requestRecord?.total || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Pending Requests",
          value: reportData?.reportData?.requestRecord?.pending || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Rejected by Admin",
          value: reportData?.reportData?.requestRecord?.adminRejected || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Accepted by Admin",
          value: reportData?.reportData?.requestRecord?.adminAccepted || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Accepted by Users",
          value:
            reportData?.reportData?.requestRecord?.acceptedUserAccepted || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Rejected by Users",
          value:
            reportData?.reportData?.requestRecord?.acceptedUserRejected || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Pending User Response",
          value:
            reportData?.reportData?.requestRecord?.acceptedUserPending || 0,
          date: new Date().toISOString(),
        },
      ],
    },
    {
      title: "Admin Suggestions",
      metrics: [
        {
          label: "Total Suggestions Made by Admins",
          value: reportData?.reportData?.matchesRecord?.total || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Accepted by Users",
          value: reportData?.reportData?.matchesRecord?.accepted || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Rejected by Users",
          value: reportData?.reportData?.matchesRecord?.rejected || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Pending User Responses",
          value: reportData?.reportData?.matchesRecord?.pending || 0,
          date: new Date().toISOString(),
        },
      ],
    },
    {
      title: "Questionnaire Requests Summary",
      metrics: [
        {
          label: "Total Questionnaire Requests",
          value: reportData?.reportData?.questionaireRequest?.total || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Pending Questionnaire Requests",
          value: reportData?.reportData?.questionaireRequest?.pending || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Rejected by Admin",
          value:
            reportData?.reportData?.questionaireRequest?.adminRejected || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Accepted by Admin",
          value:
            reportData?.reportData?.questionaireRequest?.adminAccepted || 0,
          date: new Date().toISOString(),
        },
        {
          label: "Accepted by Users",
          value:
            reportData?.reportData?.questionaireRequest?.acceptedUserAccepted ||
            0,
          date: new Date().toISOString(),
        },
        {
          label: "Rejected by Users",
          value:
            reportData?.reportData?.questionaireRequest?.acceptedUserRejected ||
            0,
          date: new Date().toISOString(),
        },
        {
          label: "Mutually Accepted",
          value:
            reportData?.reportData?.questionaireRequest?.mutuallyAccpted || 0,
          date: new Date().toISOString(),
        },
      ],
    },
  ];

  const filteredReportSections = reportSections.map((section) => ({
    ...section,
  }));

  useEffect(() => {
    setImagesReady(false);
    const captureAllCharts = async () => {
      const images = {};
      for (let section of filteredReportSections) {
        const node = chartRefs.current[section.title];
        if (node) {
          const canvas = await html2canvas(node);
          images[section.title] = canvas.toDataURL("image/png");
        }
      }
      setChartImages(images);
      setImagesReady(true);
    };
    // Wait for charts to render
    const timeout = setTimeout(captureAllCharts, 1500);
    return () => clearTimeout(timeout);
  }, [JSON.stringify(reportData)]);

  return (
    <>
      <div className="mt-8 space-y-8">
        {filteredReportSections.map((section, idx) => (
          <div key={idx} className="rounded-2xl bg-white p-6 shadow">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {section.title}
              </h2>
            </div>
            <div className="mt-4 flex flex-col gap-6 md:flex-row">
              {/* Metrics - Left */}
              <div className="w-full space-y-4 md:w-1/2">
                {section.metrics.map((metric, index) => (
                  <div
                    key={index}
                    className="flex justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 shadow-sm"
                  >
                    <span className="font-medium text-gray-700">
                      {metric.label}
                    </span>
                    <span className="font-bold text-blue-600">
                      {metric.value}
                    </span>
                  </div>
                ))}
              </div>
              {/* Chart - Right */}
              <div className="w-full md:w-1/2">
                <div
                  ref={(el) => (chartRefs.current[section.title] = el)}
                  style={{
                    minHeight: "450px",
                    paddingBottom: "40px",
                    overflow: "visible",
                  }}
                >
                  {section.metrics.every((metric) => metric.value === 0) ? (
                    <div className="flex h-[300px] items-center justify-center text-gray-500">
                      No data available for chart
                    </div>
                  ) : section.metrics.length <= 4 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={section.metrics}
                          dataKey="value"
                          nameKey="label"
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={5}
                          label={({ percent }) =>
                            `${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {section.metrics.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={
                                ["#8884d8", "#82ca9d", "#ffc658", "#ff8042"][
                                  index % 4
                                ]
                              }
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart
                          data={section.metrics}
                          margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                        >
                          <XAxis dataKey="label" tick={false} />
                          <YAxis />
                          <Tooltip />
                          <Bar
                            dataKey="value"
                            radius={[8, 8, 0, 0]}
                            barSize={50}
                          >
                            {section.metrics.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={
                                  [
                                    "#8884d8",
                                    "#82ca9d",
                                    "#ffc658",
                                    "#ff8042",
                                    "#FF6347",
                                    "#4B0082",
                                  ][index % 6]
                                }
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      {/* Custom Legend */}
                      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                        {section.metrics.map((entry, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-2"
                          >
                            <div
                              className="h-4 w-4 rounded-full"
                              style={{
                                backgroundColor: [
                                  "#8884d8",
                                  "#82ca9d",
                                  "#ffc658",
                                  "#ff8042",
                                  "#FF6347",
                                  "#4B0082",
                                ][index % 6],
                                verticalAlign: "middle",
                              }}
                            />
                            <span className="text-sm text-gray-600">
                              {entry.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Individual download button */}
            <div className="mt-4 flex justify-center">
              <PDFDownloadLink
                document={
                  <PDFReport
                    reportSections={[section]}
                    chartImages={chartImages}
                  />
                }
                fileName={`${section.title.replace(/\s+/g, "_").toLowerCase()}_report.pdf`}
                className={`inline-block rounded bg-yellow-500 px-4 py-2 text-white ${!imagesReady ? "cursor-not-allowed opacity-50" : ""}`}
                style={{ pointerEvents: imagesReady ? "auto" : "none" }}
                disabled={!imagesReady}
              >
                {({ loading }) =>
                  loading ? "Preparing report..." : "Download"
                }
              </PDFDownloadLink>
            </div>
          </div>
        ))}
        {/* Overall Download */}
        <div className="flex justify-center">
          <PDFDownloadLink
            document={
              <PDFReport
                reportSections={filteredReportSections}
                chartImages={chartImages}
              />
            }
            fileName="overall_report.pdf"
            className={`inline-block rounded bg-yellow-500 px-4 py-2 text-white ${!imagesReady ? "cursor-not-allowed opacity-50" : ""}`}
            style={{ pointerEvents: imagesReady ? "auto" : "none" }}
            disabled={!imagesReady}
          >
            {({ loading }) =>
              loading
                ? "Preparing overall report..."
                : "Download Overall Report"
            }
          </PDFDownloadLink>
        </div>
      </div>
    </>
  );
};

export default ReportSection;
